import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { StandardEvents } from '@shopify/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 * @property {HTMLElement} [loading] - Overlay shown while a cart fetch is in flight.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {number} */
  #cartLoadingDepth = 0;

  /**
   * `showDialog()` opens on the next frame. A second call in the same turn
   * would schedule another `showModal()` and throw once the first frame runs.
   *
   * @type {boolean}
   */
  #showDialogQueued = false;

  /**
   * True when this update opened the drawer so the loading overlay could show.
   * A failed add closes it again, matching the previous open-only-on-success behavior.
   *
   * @type {boolean}
   */
  #openedForLoading = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    // payment-terms replaces its shadow content when cart totals change, so listen
    // on this stable ancestor instead of binding to a particular CTA instance.
    this.addEventListener('click', this.#handleInstallmentsClick, true);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener('click', this.#handleInstallmentsClick, true);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Opens the drawer once per turn. The base implementation waits a frame
   * before `showModal()`, so a second synchronous call would queue another open.
   */
  showDialog() {
    if (this.refs.dialog?.open || this.#showDialogQueued) return;

    this.#showDialogQueued = true;
    this.addEventListener(
      DialogOpenEvent.eventName,
      () => {
        this.#showDialogQueued = false;
      },
      { once: true }
    );
    super.showDialog();
  }

  /**
   * Auto-open only on successful add. Defer until a source modal (e.g. quick-add)
   * closes so focus restoration stays correct. Re-measure sticky after morph.
   *
   * Cart line events are dispatched before the cart request. Show the loading
   * overlay for add, remove, and quantity updates, and open the drawer immediately
   * on add so the wait is visible.
   *
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen =
      this.hasAttribute('auto-open') && event.action === 'add' && !this.refs.dialog?.open;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first. Non-modal dialogs don't close on add — `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    const tracksCartFetch =
      Boolean(event.promise) &&
      (event.action === 'add' || event.action === 'remove' || event.action === 'update');
    const showLoading = tracksCartFetch && this.#beginCartLoading(sourceModal, event.action === 'add');

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.showDialog();
    }

    const releaseLoading = () => {
      if (!showLoading) return;

      // Cart section morph runs in a later microtask on the same promise.
      // Hide on the next frame so the overlay stays up until the new cart paints.
      requestAnimationFrame(() => this.#endCartLoading());
    };

    if (!event.promise) return;

    event.promise
      .then(({ cart, detail }) => {
        if (detail?.didError) this.#closeIfOpenedForLoading();
        else this.#openedForLoading = false;

        const settle = () => {
          this.#announceCartCount(cart?.totalQuantity ?? detail?.itemCount);
          requestAnimationFrame(() => this.#updateStickyState());
        };

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.refs.dialog?.open) this.showDialog();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        this.#closeIfOpenedForLoading();
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      })
      .finally(releaseLoading);
  };

  /**
   * Shows the drawer loading overlay before the cart request resolves.
   * Opens the drawer on add when it is closed and auto-open is enabled.
   *
   * @param {HTMLDialogElement | null} sourceModal
   * @param {boolean} isAdd
   * @returns {boolean} Whether the overlay was shown.
   */
  #beginCartLoading(sourceModal, isAdd) {
    const dialog = this.refs.dialog;
    if (!dialog) return false;

    const shouldOpen = isAdd && this.hasAttribute('auto-open') && !dialog.open && !sourceModal;

    if (!dialog.open && !shouldOpen) return false;

    this.#cartLoadingDepth += 1;
    this.refs.loading?.removeAttribute('hidden');
    dialog.setAttribute('aria-busy', 'true');

    if (shouldOpen) {
      this.#openedForLoading = true;
      this.showDialog();
    }

    return true;
  }

  /**
   * Closes a drawer that was opened only to show the loading state, after a failed add.
   */
  #closeIfOpenedForLoading() {
    if (!this.#openedForLoading) return;

    this.#openedForLoading = false;

    const close = () => {
      if (this.refs.dialog?.open) this.closeDialog();
    };

    if (this.#showDialogQueued) {
      requestAnimationFrame(close);
      return;
    }

    close();
  }

  /**
   * Hides the loading overlay after the matching cart fetch settles.
   */
  #endCartLoading() {
    if (this.#cartLoadingDepth === 0) return;

    this.#cartLoadingDepth -= 1;
    if (this.#cartLoadingDepth > 0) return;

    this.refs.loading?.setAttribute('hidden', '');
    this.refs.dialog?.removeAttribute('aria-busy');
  }

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();
  }

  close() {
    this.closeDialog();
  }

  /**
   * Closes the cart drawer before the installments dialog opens (Horizon 4.3.0).
   * @param {MouseEvent} event
   */
  #handleInstallmentsClick = (event) => {
    const path = event.composedPath();
    const ctaIndex = path.findIndex(
      (element) => element instanceof Element && element.id === 'shopify-installments-cta'
    );
    if (ctaIndex === -1) return;

    const ctaRoot = path.slice(ctaIndex + 1).find((element) => element instanceof ShadowRoot);
    if (!ctaRoot?.host?.matches?.('shopify-payment-terms')) return;

    this.closeDialog();
  };

  #updateStickyState = () => {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  };
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
